import { useEffect, useState } from "react"
import { CustomOperation } from "lib/types/generated/graphql-types"
import { useLazyQuery } from "@apollo/client"
import SEARCH_ORDER_OPERATIONS_TOTAL_HITS from "../../../graphql/queries/orderOperations/SearchOrderOperationsTotalHits"
import esb from "elastic-builder"
import dayjs from "dayjs"
import { formatOperationToInput } from "../helpers"
import { Query } from "lib/types/common"

interface UseOperationsOverTimeArgs {
  startDate: string
  endDate: string
  storeGroups: { value: string; label: string }[]
  storeMarkets: { value: string; label: string }[]
  operations: CustomOperation[]
}

export interface TimeSeriesData {
  timestamp: string
  displayLabel: string
  Delivery: number
  Cancellation: number
  Refund: number
  Release: number
  Compensation: number
  OrderLinesModification: number
  ShippingFeesModification: number
}

const useOperationsOverTimeData = ({
  startDate,
  endDate,
  storeGroups,
  storeMarkets,
  operations
}: UseOperationsOverTimeArgs) => {
  const [chartData, setChartData] = useState<TimeSeriesData[]>([])

  const formatLabel = (timestamp: string): string => {
    const date = dayjs(timestamp)
    return date.format("MMM D")
  }

  const [searchOperations, { loading, error }] = useLazyQuery(SEARCH_ORDER_OPERATIONS_TOTAL_HITS, {
    onCompleted: (data) => {
      if (data?.searchOrderOperations?.aggregations) {
        const aggregations = JSON.parse(data.searchOrderOperations.aggregations)
        const buckets = aggregations?.operationsOverTime?.buckets || []

        const formattedData: TimeSeriesData[] = buckets.map(
          (bucket: {
            key_as_string: string
            doc_count: number
            byType: { buckets: Array<{ key: string; doc_count: number }> }
          }) => {
            const dataPoint: TimeSeriesData = {
              timestamp: bucket.key_as_string,
              displayLabel: formatLabel(bucket.key_as_string),
              Delivery: 0,
              Cancellation: 0,
              Refund: 0,
              Release: 0,
              Compensation: 0,
              OrderLinesModification: 0,
              ShippingFeesModification: 0
            }

            bucket.byType?.buckets?.forEach((typeBucket) => {
              const operationType = typeBucket.key as keyof Omit<
                TimeSeriesData,
                "timestamp" | "displayLabel"
              >
              if (
                operationType === "Delivery" ||
                operationType === "Cancellation" ||
                operationType === "Refund" ||
                operationType === "Release" ||
                operationType === "Compensation" ||
                operationType === "OrderLinesModification" ||
                operationType === "ShippingFeesModification"
              ) {
                dataPoint[operationType] = typeBucket.doc_count
              }
            })

            return dataPoint
          }
        )

        setChartData(formattedData)
      }
    }
  })

  const buildQueryAndFetch = () => {
    const startDateCheck = new Date(startDate)
    const startDateIsValid = startDateCheck && !isNaN(startDateCheck.getTime())
    const endDateCheck = new Date(endDate)
    const endDateIsValid = endDateCheck && !isNaN(endDateCheck.getTime())

    if (!startDateIsValid || !endDateIsValid) return

    const boolQuery = esb
      .boolQuery()
      .must(
        esb
          .rangeQuery("completed")
          .gt(dayjs(startDate).format("YYYY-MM-DD-HH:mm"))
          .lt(dayjs(endDate).format("YYYY-MM-DD-HH:mm"))
          .format("yyyy-MM-dd-HH:mm")
      )

    if (storeGroups?.length > 0) {
      boolQuery.must(
        esb.termsQuery(
          "storeGroupId",
          storeGroups.map((option) => option.value)
        )
      )
    }

    if (storeMarkets?.length > 0) {
      boolQuery.must(
        esb.termsQuery(
          "countryCode",
          storeMarkets.map((option) => option.value)
        )
      )
    }

    if (operations.length > 0) {
      boolQuery.must(
        esb.termsQuery(
          "type",
          operations?.map((operation) => formatOperationToInput(operation))
        )
      )
    }

    const aggs = esb
      .requestBodySearch()
      .agg(
        esb
          .dateHistogramAggregation("operationsOverTime", "completed")
          .interval("1d")
          .minDocCount(0)
          .extendedBounds(dayjs(startDate).valueOf(), dayjs(endDate).valueOf())
          .agg(esb.termsAggregation("byType", "type"))
      )
      .toJSON() as Query

    const dailyQuery = esb.requestBodySearch().query(boolQuery)
    const query = dailyQuery.toJSON() as Query

    if (query) {
      searchOperations({
        variables: {
          from: 0,
          size: 0,
          query: JSON.stringify({
            bool: {
              must: [query.query]
            }
          }),
          trackTotalHits: false,
          aggs: JSON.stringify(aggs.aggs)
        }
      })
    }
  }

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      buildQueryAndFetch()
    }, 300)

    return () => clearTimeout(delayDebounceFn)
  }, [startDate, endDate, storeGroups, storeMarkets, operations])

  return { chartData, loading, error, refetch: buildQueryAndFetch }
}

export default useOperationsOverTimeData
